import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { useEffect, useRef } from "react";

// Layout
import AppShell from "./components/layout/AppShell";
import LoginScreen from "./components/common/LoginScreen";

// Pages
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import FinancialsPage from "./pages/FinancialsPage";
import ExploreFilesPage from "./pages/ExploreFilesPage";
import RecordingsPage from "./pages/RecordingsPage";
import RecordingDetailPage from "./pages/RecordingDetailPage";
import AIAutomationsPage from "./pages/AIAutomationsPage";
import DrawingEvaluationPage from "./pages/DrawingEvaluationPage";
import MicrosoftCallbackPage from "./pages/MicrosoftCallbackPage";
import ConnectorCallbackPage from "./pages/ConnectorCallbackPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import SettingsPage from "./pages/SettingsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForcedPasswordResetScreen from "./components/common/ForcedPasswordResetScreen";
import ChatPage from "./pages/ChatPage";
import HelpPage from "./pages/HelpPage";

// Admin
import AdminLayout from "./components/admin/AdminLayout";
import AdminLoginScreen from "./components/admin/AdminLoginScreen";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsageCostPage from "./pages/admin/AdminUsageCostPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminOrganizationsPage from "./pages/admin/AdminOrganizationsPage";
import AdminAuditPage from "./pages/admin/AdminAuditPage";

// Public auth pages
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";

// Auth
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import NotificationTray from "./components/notifications/NotificationTray";
import { isAdminRole } from "./utils/roles";

// Re-export API base so legacy imports still work
export { API } from "./api/client";

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="spinner" />
    </div>
  );
}

function WelcomeToast({ isNew }) {
  const shown = useRef(false);
  useEffect(() => {
    if (isNew && !shown.current) {
      shown.current = true;
      toast.success("Welcome! Your workspace has been created.", {
        description:
          "Head to AI Automations to grant server access and connect your Microsoft account.",
        duration: 6000,
      });
    }
  }, [isNew]);
  return null;
}

/**
 * AdminRoute - Guard for admin-only routes
 * - Unauthenticated users → /admin/login
 * - Authenticated non-admins → / (main portal)
 */
function AdminRoute({ children }) {
  const { user } = useAuth();

  // Still loading
  if (user === undefined) {
    return <FullScreenSpinner />;
  }

  // Not logged in → redirect to admin login
  if (user === null) {
    return <Navigate to="/admin/login" replace />;
  }

  // Logged in but not an admin → redirect to main portal
  if (!isAdminRole(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AuthedApp() {
  const { user, signOut } = useAuth();

  // Mandatory interceptor: an admin generated a temporary password for this
  // user (or they haven't set one yet) — block everything else until they
  // set a permanent password. Covers both the admin portal and main portal
  // since this component backs both route trees.
  if (user?.password_reset_required) {
    return <ForcedPasswordResetScreen />;
  }

  return (
    <>
      <WelcomeToast isNew={user?.is_new} />
      <NotificationTray />
      <Routes>
        {/* OAuth callback — outside AppShell */}
        <Route path="/microsoft-callback" element={<MicrosoftCallbackPage />} />
        <Route path="/connector-callback" element={<ConnectorCallbackPage />} />

        {/* Authenticated app routes */}
        <Route
          path="/*"
          element={
            <AppShell onLogout={signOut}>
              <Routes>
                <Route path="/" element={<ProjectsPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/financials" element={<FinancialsPage />} />
                <Route path="/files" element={<ExploreFilesPage />} />
                <Route path="/recordings" element={<RecordingsPage />} />
                <Route path="/recordings/:id" element={<RecordingDetailPage />} />
                <Route path="/automations" element={<AIAutomationsPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route
                  path="/admin/*"
                  element={
                    <AdminRoute>
                      <AdminLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="usage-and-cost" element={<AdminUsageCostPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="organizations" element={<AdminOrganizationsPage />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                </Route>
                <Route
                  path="/drawing-evaluation"
                  element={<DrawingEvaluationPage onLogout={signOut} />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          }
        />
      </Routes>
    </>
  );
}

function AppRoot() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Allow the Microsoft OAuth callback regardless of auth state
  if (pathname === "/microsoft-callback") {
    return <MicrosoftCallbackPage />;
  }

  // Admin-assisted reset hint — accessible regardless of auth state
  if (pathname === "/forgot-password") {
    return <ForgotPasswordPage />;
  }

  // Accept invitation — public route, no auth required
  if (pathname.startsWith("/accept-invite/")) {
    return (
      <Routes>
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      </Routes>
    );
  }

  // ── Admin portal namespace (fully separate from the user portal) ───────────
  // The dedicated admin login is the ONLY entry to the admin area.
  if (pathname === "/admin/login") {
    // An already-authenticated admin shouldn't see the login form.
    if (user && isAdminRole(user.role)) return <Navigate to="/admin" replace />;
    return <AdminLoginScreen />;
  }
  if (pathname.startsWith("/admin")) {
    if (user === undefined) return <FullScreenSpinner />;
    // Not logged in → redirect to admin login
    if (user === null) {
      return <Navigate to="/admin/login" replace />;
    }
    // Logged in but not an admin → redirect to main portal
    if (!isAdminRole(user.role)) {
      return <Navigate to="/" replace />;
    }
    return <AuthedApp />;
  }

  // ── Normal user portal ─────────────────────────────────────────────────────
  if (user === undefined) return <FullScreenSpinner />;
  if (user === null) return <LoginScreen />;
  return <AuthedApp />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationsProvider>
          <div className="App">
            <Toaster
              position="top-right"
              toastOptions={{
                style: { fontFamily: "IBM Plex Sans, sans-serif" },
              }}
            />
            <AppRoot />
          </div>
        </NotificationsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
